import {
  Button,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  makeStyles,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import AccessProfileModel from "api/models/AccessProfileModel";
import { HeadTable, Row } from "components/UI/Table";
import { lang } from "i18nLang";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { AccessProfileStore, CompanyStore } from "store";
import AccessProfileForm from "./AccessProfileForm";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: "65px 0px 0px",
    height: "80vh",
    overflow: "hidden",
    overflowY: "scroll",
  },
  titleContainer: { marginBottom: 25, height: 52 },
  addButtonIcon: { marginRight: 5, marginTop: -3 },
  tableHeadCell: {
    "& th": { color: theme.palette.gray[700], fontWeight: "500 !important" },
  },
}));

const AccessProfiles = observer(() => {
  const classes = useStyles();
  const [showForm, setShowForm] = useState(false);
  const [selectedProfile, setSelectedProfile] =
    useState<AccessProfileModel | null>(null);
  const profiles = AccessProfileStore.accessProfiles;
  const columns = [
    { dataKey: "name", label: lang("profile") },
    { dataKey: "quantityUsers", label: lang("users"), width: 300 },
  ];

  useEffect(() => {
    CompanyStore.getCompanysUsers().then(() =>
      AccessProfileStore.subscribeAccessProfiles()
    );

    return () => {
      AccessProfileStore.unsubscribeAccessProfiles();
    };
  }, []);

  const handleClickCreate = () => {
    setSelectedProfile(null);
    setShowForm(true);
  };

  const handleCloseForm = () => setShowForm(false);

  const handleClickEdit = (profile: AccessProfileModel) => {
    setSelectedProfile(profile);
    setShowForm(true);
  };

  return (
    <>
      <Grid
        container
        item
        xs
        className={classes.container}
        justifyContent="center"
        alignItems="flex-start"
      >
        {showForm && (
          <AccessProfileForm
            selectedProfile={selectedProfile}
            onClose={handleCloseForm}
          />
        )}
        {!showForm && (
          <Grid container item xs={9}>
            <Grid
              container
              item
              xs={12}
              className={classes.titleContainer}
              justifyContent="space-between"
            >
              <Typography variant="h4" component="h2" gutterBottom>
                {CompanyStore.currentCompany?.name || ""}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={handleClickCreate}
              >
                <AddIcon className={classes.addButtonIcon} />
                {lang("userProfile.addProfile")}
              </Button>
            </Grid>
            <Grid container item xs={12}>
              <TableContainer component={Paper}>
                <Table>
                  <HeadTable
                    className={classes.tableHeadCell}
                    columns={columns}
                    actionColumn={true}
                    actionWidth={140}
                    actionAlign="center"
                  />
                  <TableBody>
                    {profiles.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3} align="center">
                          {lang("userProfile.noProfileFound")}
                        </TableCell>
                      </TableRow>
                    )}
                    {profiles.map((profile, index) => {
                      const data = {
                        name: profile.name,
                        isAdmin: profile.isAdmin(),
                        quantityUsers: lang(
                          "userProfile.users",
                          profile.quantityUsers
                        ),
                      };
                      return (
                        <Row
                          key={index}
                          columns={columns}
                          row={data}
                          actionCell={
                            <TableCell align="center">
                              <Button
                                color="primary"
                                onClick={() => handleClickEdit(profile)}
                              >
                                {lang(profile.isAdmin() ? "view" : "edit")}
                              </Button>
                            </TableCell>
                          }
                        />
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        )}
      </Grid>
    </>
  );
});

export default AccessProfiles;
